/**
 * Copy text and report whether it worked. Resolves true or false and never rejects.
 *
 * navigator.clipboard.writeText() rejects whenever the browser refuses the write:
 * a denied clipboard-write permission (headless Chrome, locked-down browsers), a
 * document without focus, or an iframe without the permission. A caller that does
 * not handle that rejection leaks an unhandled promise rejection to Flare and, if it
 * sets its "Copied" state on its own, tells the user the copy worked when it did not.
 * Every copy control should go through this and branch on the result.
 *
 * `env` exists for the tests; in the browser it is the window.
 */
export async function copyToClipboard(text, env = globalThis) {
    const clipboard = env.navigator?.clipboard;

    if (clipboard && typeof clipboard.writeText === 'function' && env.isSecureContext) {
        try {
            await clipboard.writeText(String(text));
            return true;
        } catch (e) {
            // Refused. The legacy path below is gated differently and often still works.
        }
    }

    return legacyCopy(String(text), env.document);
}

function legacyCopy(text, document) {
    if (!document?.body || typeof document.execCommand !== 'function') {
        return false;
    }

    // select() takes focus and removing the textarea drops it on <body>, so a keyboard
    // user would otherwise have to tab back from the top of the page.
    const trigger = document.activeElement;
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    // Inside a modal, x-trap pulls focus back out of anything appended to <body>, which
    // leaves nothing selected; Chrome's execCommand('copy') still returns true then.
    const host = (typeof trigger?.closest === 'function' && trigger.closest('[role="dialog"]')) || document.body;
    host.appendChild(textarea);
    textarea.select();

    try {
        if (document.activeElement !== textarea) {
            return false;
        }
        // execCommand reports a refusal by returning false; some browsers throw instead.
        return document.execCommand('copy') === true;
    } catch (e) {
        return false;
    } finally {
        textarea.remove();
        if (trigger && typeof trigger.focus === 'function') {
            trigger.focus();
        }
    }
}

/**
 * Tell a screen reader what happened to a copy, through the page-level
 * #aaa-copy-status region in layouts/app and layouts/guest.
 *
 * Measured with VoiceOver in Chrome on PR 2595: a status line inside a panel that was
 * display:none at page load is never announced, and a region that gets the same text
 * again is not re-announced, even when it is cleared in between. So the region lives at
 * page level, and every other message carries a trailing no-break space so a repeat
 * really differs.
 *
 * Inside a dialog the page-level region is not enough. NatalieMac's VoiceOver session on
 * PR 2595 did not hear it from the MCP token modal, with or without aria-modal, while a
 * polite region inside the open dialog was heard. So when focus is in a [role="dialog"]
 * that carries its own [data-aaa-copy-status] region, the message goes there instead.
 * Inside a dialog that region must not carry aria-atomic="true": with it VoiceOver read
 * nothing, and without it every refusal was read (PR 2595 round 4). The page-level
 * region keeps aria-atomic and is read on the card.
 */
export function announceCopyStatus(message, env = globalThis) {
    const doc = env.document;
    const page = doc?.getElementById?.('aaa-copy-status') ?? null;
    const active = doc?.activeElement;
    const dialog = typeof active?.closest === 'function' ? active.closest('[role="dialog"]') : null;
    const region = dialog?.querySelector?.('[data-aaa-copy-status]') || page;
    if (!region) {
        return false;
    }

    // With no message, the page region's translated default ("Copying was blocked...").
    const text = message ?? page?.dataset.blockedMessage ?? region.dataset.blockedMessage;
    if (!text) {
        return false;
    }

    const pad = region.dataset.aaaPad !== '1';
    region.dataset.aaaPad = pad ? '1' : '0';
    region.textContent = String(text) + (pad ? ' ' : '');

    return true;
}
